import React from "react";
import { Helmet } from "react-helmet";
import { ShareSection } from "../../components/template";

const Blog7 = () => {
  const hashtags = [
    "#selfgrowth",
    "#mindfulliving",
    "#ParamLife",
    "#consciouschoices",
    "#wellnessjourney",
  ];

  return (
    <>
      {/* 🧠 SEO Meta Tags */}
      <Helmet>
        <title>Are You Choosing Your Life — Or Just Settling For It?</title>
        <meta
          name="title"
          content="Are You Choosing Your Life — Or Just Settling For It?"
        />
        <meta
          name="description"
          content="Feeling stuck or drained? Learn how small conscious choices can help you stop settling and start truly living the life you want."
        />
        <meta
          name="keywords"
          content="self growth, conscious living, stop settling, Param Life, personal transformation, toxic habits, change your life, mindfulness, Indian wellness, mental clarity"
        />
        <meta name="author" content="Param Life" />
        <meta
          property="og:title"
          content="Are You Choosing Your Life — Or Just Settling For It?"
        />
        <meta
          property="og:description"
          content="Feeling stuck or drained? Learn how small conscious choices can help you stop settling and start truly living the life you want."
        />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Param Life" />
        <meta property="og:locale" content="en_IN" />
      </Helmet>

      {/* 🌿 Blog Body */}
      <article className="max-w-4xl mx-auto mt-10 px-6 py-4 leading-relaxed text-gray-800">
        {/* Hero Image */}

        {/* Title + Subtitle */}
        <h1 className="text-3xl font-bold text-secondary mb-8 ">
           Are You Choosing Your Life — Or Just Settling For It?
        </h1>
                <img
          src="/assets/knowyourself/blogs/blog7.jpg"
          alt="Are You Choosing Your Life — Or Just Settling For It?"
          className="w-[80%] max-w-xl rounded-2xl shadow-md mx-auto mb-10"
        />

        <p className="text-[#578d48] text-center italic mb-8">
          Because comfort can be the most dangerous trap of all.
        </p>

        {/* 💫 Intro */}
        <section className="space-y-4 mb-8">
          <h2 className="text-2xl font-semibold text-primary">
            💫 The Silent Trap of “It’s Fine”
          </h2>
          <p>
            <span className="text-[#b91c1c] font-semibold">“It’s fine.”</span>{" "}
            Two simple words that quietly kill growth. You tell yourself this
            when you stay in a job that drains you. You tell yourself this when
            you keep repeating the same habits that make you feel stuck. You
            tell yourself this when your heart whispers “there’s more” but your
            mind says, “maybe later.”
          </p>
          <p>
            But here’s the truth — most of the situations we feel trapped in are
            not permanent. They’re simply the result of our own choices. Not
            always made consciously, of course. Many of them were made out of
            fear, comfort, or social conditioning. But still — our choices.
          </p>
          <p>
            The good news? If you chose your way into a mess… you can also
            choose your way out.
          </p>
        </section>

        {/* 🌿 Section 2 */}
        <section className="space-y-4 mb-8">
          <h2 className="text-2xl font-semibold text-primary">
            🌿 Choice vs. Compromise: The Real Battle
          </h2>
          <p>
            Every single day, you’re standing at a small crossroad. Sometimes
            it’s about what to eat, who to talk to, or what to tolerate. Other
            times, it’s about bigger things — career, relationships, lifestyle,
            peace.
          </p>
          <p>
            In each of these moments, you have two paths:{" "}
            <span className="text-[#578d48] font-semibold">
              👉 Change consciously.
            </span>{" "}
            <span className="text-[#b91c1c] font-semibold">
              👉 Or compromise endlessly.
            </span>
          </p>
          <p>
            Change feels scary at first. It demands discomfort, awareness, and
            responsibility. While compromise feels easy — it lets you stay where
            you are, blaming circumstances.
          </p>
          <p>
            But here’s the painful truth most people never face: when you don’t
            choose consciously, you still choose — by default. And the cost of
            default choices is regret.
          </p>
        </section>

        {/* 🔍 Section 3 */}
        <section className="space-y-4 mb-8">
          <h2 className="text-2xl font-semibold text-primary">
            🔍 Why We Keep Settling (Even When We’re Aware)
          </h2>
          <p>
            You already know what’s not working in your life. You’ve probably
            said things like:
          </p>
          <ul className="list-disc list-inside space-y-2">
            <li>“I want to quit this habit.”</li>
            <li>“I want to leave this toxic place.”</li>
            <li>“I want to live a healthier, fuller life.”</li>
          </ul>
          <p>
            And yet — nothing changes. Why? Because awareness isn’t enough. Your
            brain craves familiarity more than happiness. Even the pain you know
            feels safer than the unknown that might free you.
          </p>
          <p>
            So you stay. You tolerate. You label survival as strength — and call
            compromise “maturity.” But deep down, you know: you’re not at peace.
            You’re just comfortable in discomfort.
          </p>
        </section>

        {/* ⚖️ Section 4 */}
        <section className="space-y-4 mb-8">
          <h2 className="text-2xl font-semibold text-primary">
            ⚖️ Comfort vs. Growth — You Can’t Have Both
          </h2>
          <p>
            Comfort feels like safety, but it quietly dulls your inner fire.
            Growth feels uncertain, but it reawakens your spirit. When you start
            making conscious choices — small ones, every day — something magical
            happens:
          </p>
          <ul className="list-disc list-inside space-y-2">
            <li>Your self-respect grows.</li>
            <li>Your energy rises.</li>
            <li>
              You start feeling proud of the way you live, not just the way you
              look.
            </li>
          </ul>
          <p className="text-[#4b5563] italic">
            Peace doesn’t come from things staying the same — it comes from
            finally aligning your actions with what feels true inside.
          </p>
        </section>

        {/* 🌞 Section 5 */}
        <section className="space-y-4 mb-8">
          <h2 className="text-2xl font-semibold text-primary">
            🌞 Start Small: Conscious Shifts That Rebuild Your Life
          </h2>
          <p>
            Change doesn’t need to be a dramatic “life reset.” It’s built
            through tiny, consistent shifts that compound over time. Here are a
            few to start with:
          </p>
          <ol className="list-decimal list-inside space-y-2">
            <li>
              <strong className="text-[#578d48]">
                Change your environment — one corner at a time.
              </strong>{" "}
              Clean your space, light incense, bring in sunlight. Your outer
              environment silently shapes your inner one.
            </li>
            <li>
              <strong className="text-[#578d48]">
                Replace reaction with reflection.
              </strong>{" "}
              Before making a big decision, pause and ask: “Am I choosing this
              out of fear, or out of truth?” You’ll be surprised how many of
              your “musts” are actually “shoulds.”
            </li>
            <li>
              <strong className="text-[#578d48]">
                Stop waiting for motivation. Build momentum instead.
              </strong>{" "}
              Start with one small action every day — even if it’s imperfect.
              Clarity comes from movement, not overthinking.
            </li>
            <li>
              <strong className="text-[#578d48]">
                Spend more time with people who inspire change, not complain
                about it.
              </strong>{" "}
              Your energy becomes the average of who you’re surrounded by. Choose
              wisely — your circle is your mirror.
            </li>
            <li>
              <strong className="text-[#578d48]">Reconnect with your body.</strong>{" "}
              Movement, breathwork, and silence are not “wellness hacks.” They’re
              how you remember who you are beneath all noise.
            </li>
          </ol>
        </section>

        {/* 🪞 Section 6 */}
        <section className="space-y-4 mb-8">
          <h2 className="text-2xl font-semibold text-primary">
            🪞 Ask Yourself These Questions Today:
          </h2>
          <ul className="list-disc list-inside space-y-2">
            <li>Where am I choosing comfort over growth?</li>
            <li>What do I keep tolerating that drains me?</li>
            <li>What version of myself am I afraid to outgrow?</li>
            <li>If I were truly free, what would I do differently today?</li>
          </ul>
          <p>
            Take a moment with these questions — not to judge yourself, but to
            understand. Because self-awareness without action is just another
            form of escape.
          </p>
        </section>

        {/* 💔 Section 7 */}
        <section className="space-y-4 mb-8">
          <h2 className="text-2xl font-semibold text-primary">
            💔 When You Don’t Choose, Life Chooses for You
          </h2>
          <p>
            Every delay, every “someday,” every “not now” — adds weight to your
            soul. The job you hate becomes your identity. The relationship that
            limits you becomes your prison. The habit that once helped you cope
            starts controlling you.
          </p>
          <p>
            And you wake up one day realizing… you never chose this life. You
            just stayed where it was easier to stay. But here’s what’s beautiful
            — every single morning, life gives you another choice. To start
            again. To speak truth. To realign.{" "}
            <span className="text-[#578d48] font-semibold">
              No matter how long you’ve been settling, the door to change never
              locks.
            </span>
          </p>
        </section>

        {/* 🌸 Section 8 */}
        <section className="space-y-4 mb-8">
          <h2 className="text-2xl font-semibold text-primary">
            🌸 The Subtle Power of Choosing Yourself
          </h2>
          <p>
            Choosing yourself doesn’t mean you quit everything and run away. It
            means you stop abandoning your peace for temporary comfort. It means
            you say no to things that look good but feel wrong. It means you
            prioritize inner clarity over outer approval.
          </p>
          <p>It means you stop existing — and start living intentionally.</p>
          <p className="text-[#4b5563] italic">
            Once you start choosing from awareness instead of fear, life stops
            feeling heavy. It starts feeling like yours again.
          </p>
        </section>

        {/* 🌼 Final Thought */}
        <section className="space-y-4 mb-8">
          <h2 className="text-2xl font-semibold text-primary">
            🌼 Final Thought
          </h2>
          <p>
            Change doesn’t start with big leaps — it starts with small, honest
            choices. The kind that no one notices, but your soul does.
          </p>
          <p>
            Today, you have the same two options:{" "}
            <span className="text-[#578d48] font-semibold">
              👉 Keep compromising.
            </span>{" "}
            <span className="text-[#b91c1c] font-semibold">
              👉 Or start changing — consciously.
            </span>{" "}
            You already know which one your heart wants.
          </p>
          <p className="text-[#4b5563] italic">
            So take that tiny, brave step — the one that whispers, “I’m ready to
            choose myself now.”
          </p>
        </section>

        {/* Hashtags */}
        <div className="flex flex-wrap gap-2 mt-10">
          {hashtags.map((tag, idx) => (
            <span
              key={idx}
              className="bg-red-700 bg-opacity-10 text-red-700 hover:bg-red-700 hover:text-white rounded-full px-3 py-1 text-sm transition"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Share Section */}
        <div className="mt-8">
          <ShareSection />
        </div>
      </article>
    </>
  );
};

export default Blog7;
